import duckLookingGif from "../../../assets/utya-looking.gif";
import { displayMoney, moneyUnits } from "../lib/walletMoney";
import { LockBadgeIcon, OperationArrowIcon } from "./Balance";

export type WalletOperation = {
  id: string;
  kind?: string | null;
  title: string;
  currency?: string | null;
  amount?: string | null;
  amount_rub?: string | null;
  amount_usdt?: string | null;
  rate_rub_per_usdt?: string | null;
  rate_rub_usdt?: string | null;
  confirmed_at?: string | null;
  source?: string | null;
  mode?: string | null;
  method?: string | null;
  details?: string | null;
  status: string;
  created_at: string;
};

type Props = {
  operations: WalletOperation[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  busy: boolean;
  cancelId: number | null;
  onRefresh: () => void;
  onLoadMore: () => void;
  onAskCancel: (id: number) => void;
  onDismissCancel: () => void;
  onConfirmCancel: (id: number) => void;
};

export function DriverWalletHistory({
  operations,
  loading,
  error,
  hasMore,
  busy,
  cancelId,
  onRefresh,
  onLoadMore,
  onAskCancel,
  onDismissCancel,
  onConfirmCancel,
}: Props) {
  const groups = groupOperationsByDay(operations);
  return (
    <div className="tp-balance-history-panel tp-balance-history-panel-page">
      <div className="tp-withdraw-refresh-row">
        <button type="button" disabled={busy || loading} onClick={onRefresh}>
          {loading ? "загружаем..." : "обновить историю"}
        </button>
      </div>
      {error && (
        <p className="tp-alert" role="alert">
          {error}
        </p>
      )}
      {loading && operations.length === 0 && (
        <p className="tp-withdraw-course-note" role="status">
          загружаем историю...
        </p>
      )}
      {!loading && !error && operations.length === 0 && (
        <div className="tp-balance-empty-card">
          <p className="tp-balance-empty-title">история пустая</p>
          <img
            src={duckLookingGif}
            alt=""
            className="tp-balance-empty-duck"
            draggable={false}
          />
          <p className="tp-balance-empty-text">
            когда вы начнете совершать транзакции, они появятся здесь
          </p>
        </div>
      )}
      {operations.length > 0 && (
        <div className="tp-balance-history-groups">
          {groups.map((group) => (
            <div key={group.key} className="tp-balance-history-group">
              <div className="tp-balance-history-date">{group.label}</div>
              <ul className="tp-balance-history-list">
                {group.items.map((operation) => {
                  const conversion = isConversion(operation);
                  const withdrawalId = getWithdrawalId(operation);
                  const legacy =
                    withdrawalId !== null && operation.currency !== "USDT";
                  const canCancel =
                    withdrawalId !== null && operation.status === "pending";
                  const confirming = canCancel && cancelId === withdrawalId;
                  const rate =
                    conversion || legacy ? savedRate(operation) : null;
                  const status = statusLabel(operation.status);
                  return (
                    <li key={operation.id} className="tp-balance-operation-row">
                      <div className="tp-balance-operation-icon-wrap">
                        <OperationArrowIcon />
                        {canCancel && (
                          <span
                            className="tp-balance-operation-lock"
                            aria-hidden="true"
                          >
                            <LockBadgeIcon />
                          </span>
                        )}
                      </div>
                      <div className="tp-balance-operation-main">
                        <div className="tp-balance-operation-title">
                          {conversion
                            ? "конвертация в USDT"
                            : legacy
                              ? "старая заявка на вывод"
                              : withdrawalId !== null
                                ? status || "вывод средств"
                                : operation.title}
                        </div>
                        <div className="tp-balance-operation-time">
                          {formatDateTime(operation.created_at)}
                          {legacy && status ? ` · ${status}` : ""}
                        </div>
                        {canCancel && (
                          <div className="tp-balance-operation-actions">
                            {confirming ? (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={onDismissCancel}
                                >
                                  не отменять
                                </button>
                                <button
                                  type="button"
                                  className="tp-balance-operation-cancel-confirm"
                                  disabled={busy}
                                  aria-busy={busy}
                                  onClick={() => onConfirmCancel(withdrawalId)}
                                >
                                  {busy ? "отменяем..." : "да, отменить"}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => onAskCancel(withdrawalId)}
                              >
                                отменить заявку
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="tp-balance-operation-side">
                        <div className="tp-balance-operation-amount">
                          {operationAmount(
                            operation,
                            conversion,
                            withdrawalId !== null,
                          )}
                        </div>
                        {conversion && operation.amount_rub != null && (
                          <div className="tp-balance-operation-course">
                            из {briefAbsolute(operation.amount_rub)} ₽
                          </div>
                        )}
                        {legacy && operation.amount_usdt != null && (
                          <div className="tp-balance-operation-course">
                            = {briefAbsolute(operation.amount_usdt)} USDT
                          </div>
                        )}
                        {rate && (
                          <div className="tp-balance-operation-course">
                            курс {displayMoney(rate, true)} ₽/USDT
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          type="button"
          className="tp-button-primary tp-withdraw-request-button mt-3 w-full"
          disabled={loading || busy}
          onClick={onLoadMore}
        >
          {loading ? "загружаем..." : "загрузить ещё"}
        </button>
      )}
    </div>
  );
}

function isConversion(operation: WalletOperation) {
  return operation.kind === "conversion" || operation.kind === "fx_conversion";
}

function getWithdrawalId(operation: WalletOperation): number | null {
  if (operation.kind !== "withdrawal" && operation.kind != null) return null;
  const match = /^withdrawal[-:](\d+)$/.exec(operation.id);
  const id = match ? Number(match[1]) : null;
  return id !== null && Number.isSafeInteger(id) && id > 0 ? id : null;
}

function briefAbsolute(value: string) {
  return displayMoney(value.trim().replace(/^-/, ""), true);
}

function operationAmount(
  operation: WalletOperation,
  conversion: boolean,
  withdrawal: boolean,
) {
  if (conversion) return `+${briefAbsolute(operation.amount_usdt ?? "0")} USDT`;
  if (withdrawal) {
    return operation.currency === "USDT"
      ? `${briefAbsolute(operation.amount ?? operation.amount_usdt ?? "0")} USDT`
      : `${briefAbsolute(operation.amount_rub ?? operation.amount ?? "0")} ₽`;
  }
  const rub = operation.currency === "RUB" || operation.kind === "accrual";
  const amount =
    (rub ? operation.amount_rub : operation.amount_usdt) ??
    operation.amount ??
    "0";
  const sign =
    operation.kind === "accrual" && (moneyUnits(amount) ?? 0n) > 0n ? "+" : "";
  return `${sign}${displayMoney(amount, true)} ${rub ? "₽" : "USDT"}`;
}

function savedRate(operation: WalletOperation): string | null {
  const explicit = operation.rate_rub_per_usdt ?? operation.rate_rub_usdt;
  if (explicit) return explicit;
  if (isConversion(operation)) return null;
  // Legacy API хранит рубли и сумму выплаты; показываем их историческое
  // соотношение. Текущий курс и Number в финансовом отображении не участвуют.
  const rub = moneyUnits(operation.amount_rub ?? "");
  const usdt = moneyUnits(operation.amount_usdt ?? "");
  if (rub === null || usdt === null || usdt === 0n) return null;
  const rate =
    ((rub < 0n ? -rub : rub) * 100000000n) / (usdt < 0n ? -usdt : usdt);
  return `${rate / 100000000n}.${String(rate % 100000000n).padStart(8, "0")}`;
}

function statusLabel(status: string): string | null {
  return (
    (
      {
        pending: "на модерации",
        approved: "одобрена",
        cancelled: "отменено",
        rejected: "отклонено",
      } as Record<string, string>
    )[status] ?? null
  );
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "numeric",
    month: "long",
  })} ${date.toLocaleTimeString("ru-RU", {
    timeZone: "Europe/Moscow",
    hour: "2-digit",
    minute: "2-digit",
  })} мск`;
}

function groupOperationsByDay(operations: WalletOperation[]) {
  const groups = new Map<
    string,
    { key: string; label: string; items: WalletOperation[] }
  >();
  for (const operation of operations) {
    const date = new Date(operation.created_at);
    const valid = !Number.isNaN(date.getTime());
    const key = valid
      ? date.toLocaleDateString("en-CA", {
          timeZone: "Europe/Moscow",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : operation.created_at;
    const group = groups.get(key);
    if (group) group.items.push(operation);
    else
      groups.set(key, {
        key,
        label: valid
          ? date.toLocaleDateString("ru-RU", {
              timeZone: "Europe/Moscow",
              day: "numeric",
              month: "long",
            })
          : operation.created_at,
        items: [operation],
      });
  }
  return Array.from(groups.values());
}
